import { STUDENT_NOTIFICATION_RECEIVED } from './studentNotificationEvents';

/** Window event when a live-quiz FCM payload arrives (foreground/background). */
export const LIVE_QUIZ_RECEIVED = 'live-quiz-received';

/** Window event when socket delivers an updated leaderboard for a session. */
export const QUIZ_LEADERBOARD_UPDATED = 'quiz-leaderboard-updated';

/** FCM `data.event` values from Live_Quiz_Notification_Events.md */
export const LIVE_QUIZ_EVENT = {
    SESSION_INVITE: 'session_invite',
    SESSION_STARTED: 'session_started',
    QUESTION_ACTIVE: 'question_active',
    ANSWER_REVEALED: 'answer_revealed',
    LEADERBOARD: 'leaderboard',
    SESSION_COMPLETED: 'session_completed',
    SESSION_CLOSED: 'session_closed'
};

/**
 * @param {Record<string, string>|undefined} data
 * @returns {{ event: string, sessionId: string, navigateTo: string, quizTitle: string, raw: Record<string, string> }|null}
 */
export function parseLiveQuizPayload(data) {
    if (!data || typeof data !== 'object') return null;
    const type = String(data.type || '').toLowerCase();
    if (type !== 'live_quiz' && type !== 'live-quiz') {
        const event = data.event;
        const sessionId = data.sessionId || data.session_id;
        if (!event || !sessionId) return null;
    }
    const event = String(data.event || '').trim();
    const sessionId = String(data.sessionId || data.session_id || '').trim();
    if (!event || !sessionId) return null;
    return {
        event,
        sessionId,
        navigateTo: String(data.navigateTo || data.navigate_to || 'quiz').trim(),
        quizTitle: String(data.quizTitle || data.quiz_title || '').trim(),
        raw: Object.fromEntries(
            Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)])
        )
    };
}

/**
 * @param {import('firebase/messaging').MessagePayload|{ data?: Record<string, string> }} payload
 * @returns {ReturnType<typeof parseLiveQuizPayload>}
 */
export function parseLiveQuizFromFcmPayload(payload) {
    return parseLiveQuizPayload(payload?.data);
}

/**
 * Dispatch live-quiz + generic student notification events.
 * @param {ReturnType<typeof parseLiveQuizPayload>} detail
 */
export function dispatchLiveQuizReceived(detail) {
    if (typeof window === 'undefined' || !detail) return;
    window.dispatchEvent(new CustomEvent(LIVE_QUIZ_RECEIVED, { detail }));
    window.dispatchEvent(new CustomEvent(STUDENT_NOTIFICATION_RECEIVED, {
        detail: {
            title: detail.raw?.title,
            body: detail.raw?.body,
            notificationId: detail.raw?.notificationId,
            ...detail
        }
    }));
}

/**
 * @param {string} sessionId
 * @param {unknown[]} leaderboard
 */
export function dispatchQuizLeaderboardUpdated(sessionId, leaderboard) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(QUIZ_LEADERBOARD_UPDATED, {
        detail: { sessionId, leaderboard: Array.isArray(leaderboard) ? leaderboard : [] }
    }));
}

/**
 * Map FCM event to recommended UI phase (for in-quiz screens).
 * @param {string} event
 * @returns {'lobby'|'active_question'|'timer_expired'|'leaderboard'|'completed'|null}
 */
export function liveQuizEventToPhase(event) {
    switch (event) {
        case LIVE_QUIZ_EVENT.SESSION_INVITE:
        case LIVE_QUIZ_EVENT.SESSION_STARTED:
            return 'lobby';
        case LIVE_QUIZ_EVENT.QUESTION_ACTIVE:
            return 'active_question';
        case LIVE_QUIZ_EVENT.ANSWER_REVEALED:
            return 'timer_expired';
        case LIVE_QUIZ_EVENT.LEADERBOARD:
            return 'leaderboard';
        case LIVE_QUIZ_EVENT.SESSION_COMPLETED:
        case LIVE_QUIZ_EVENT.SESSION_CLOSED:
            return 'completed';
        default:
            return null;
    }
}
